// @flow

import i18n from "i18next";
import k from "src/i18n/keys";
import { connect } from "react-redux";
import React, { useRef, useCallback, useMemo } from "react";

import Options from "./Options";
import { Status as StyledStatus } from "./styles";
import { toggleChatroomOverlay } from "src/actions/header";
import {
  getChatroomStatus,
  getDueDate,
  getTemplateId,
  getActiveStatus,
} from "src/reducers";
import Tooltip from "src/components/Tooltip";
import useBoolean from "src/hooks/useBoolean";
import { useSelector } from "react-redux";
import moment from "moment";
import Badge from "./Badge";
import type { AppState, RoomId } from "src/types";

type Props = {
  roomId: RoomId,
  value: number,
  location: string,
  allowTooltip: boolean,
  chatroomOverlay?: string,
  _toggleChatroomOverlay: Function,
  customCss?: string,
};

const Status = ({
  value,
  roomId,
  location,
  allowTooltip = true,
  chatroomOverlay = "message",
  _toggleChatroomOverlay,
  customCss,
}: Props) => {
  const outerRef = useRef(null);
  const {
    value: dropdown,
    toggleBoolean: toggleDropdown,
    setFalse,
  } = useBoolean();

  const toggleStatus = useCallback(() => {
    toggleDropdown();
    _toggleChatroomOverlay(chatroomOverlay);
  }, [toggleDropdown, _toggleChatroomOverlay, chatroomOverlay]);

  const handleClose = useCallback(() => {
    setFalse();
    _toggleChatroomOverlay(chatroomOverlay);
  }, [setFalse, _toggleChatroomOverlay, chatroomOverlay]);

  const dueDate = useSelector(({ app }) => getDueDate(app, roomId));
  const templateId = useSelector(({ app }) => getTemplateId(app, roomId));
  const id = useSelector(({ app }) => getChatroomStatus(app, roomId));
  const activeStatus = useSelector(({ app }) =>
    getActiveStatus(app, templateId, id),
  );

  const overdue = useMemo(() => {
    const days = moment(dueDate)
      .startOf("day")
      .diff(moment().startOf("day"), "days");
    return days < 0;
  }, [dueDate]);

  return (
    <StyledStatus ref={outerRef} allowTooltip={allowTooltip}>
      {allowTooltip ? (
        <Tooltip title={i18n.t(k.STATUS)}>
          <Badge
            value={value}
            activeStatus={activeStatus}
            roomId={roomId}
            location={location}
            overdue={overdue}
            allowTooltip={allowTooltip}
            toggleStatus={toggleStatus}
            customCss={customCss}
          />
        </Tooltip>
      ) : (
        <Badge
          value={value}
          activeStatus={activeStatus}
          roomId={roomId}
          location={location}
          overdue={overdue}
          allowTooltip={allowTooltip}
          toggleStatus={toggleStatus}
          customCss={customCss}
        />
      )}

      {dropdown && (
        <Options
          outerRef={outerRef}
          roomId={roomId}
          show={dropdown}
          location={location}
          handleClose={handleClose}
        />
      )}
    </StyledStatus>
  );
};

const mapStateToProps = ({ app }: { app: AppState }, props) => ({
  value: getChatroomStatus(app, props.roomId),
});

export default connect(mapStateToProps, {
  _toggleChatroomOverlay: toggleChatroomOverlay,
})(Status);
